import { useQuery } from '@tanstack/react-query'
import { Check, Dumbbell, ImageIcon, LoaderCircle, Search } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { useAdminApi } from '@app/api/api-client-context'
import { exerciseKeys, type Exercise } from '@entities/exercise'
import { mediaKeys, type MediaAsset } from '@entities/media'
import { unwrapApiResponse } from '@shared/api'

const mediaPreview = (asset: MediaAsset) =>
	asset.variants?.find(variant => variant.role === 'main')?.url ?? asset.variants?.[0]?.url

export const ProgramCoverPicker = ({
	open,
	value,
	onOpenChange,
	onChange,
}: {
	open: boolean
	value?: string
	onOpenChange: (open: boolean) => void
	onChange: (assetId?: string) => void
}) => {
	const api = useAdminApi()
	const [search, setSearch] = useState('')
	const query = useQuery({
		queryKey: mediaKeys.list({ state: 'READY', search, limit: 100, surface: 'program-cover-picker' }),
		queryFn: async () => unwrapApiResponse(await api.GET('/admin/v1/media', {
			params: { query: { state: ['READY'], search: search.trim() || undefined, limit: 100 } },
		})).data,
		enabled: open,
		staleTime: 15_000,
	})
	const assets = (query.data?.items ?? []).filter(asset => asset.kind !== 'VIDEO')

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className='max-w-4xl'>
				<DialogHeader>
					<DialogTitle>Обложка программы</DialogTitle>
					<DialogDescription>Выберите готовое изображение из настоящей медиатеки.</DialogDescription>
				</DialogHeader>
				<div className='relative'>
					<Search className='pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground' />
					<Input value={search} onChange={event => setSearch(event.target.value)} placeholder='Поиск по имени файла' className='pl-9' />
				</div>
				{query.isPending ? <div className='flex items-center gap-2 py-10 text-sm text-muted-foreground'><LoaderCircle className='h-4 w-4 animate-spin' /> Загрузка медиатеки…</div> : null}
				{query.isError ? <p role='alert' className='rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800'>Не удалось загрузить медиатеку.</p> : null}
				<div className='grid max-h-[55vh] gap-3 overflow-y-auto sm:grid-cols-2 lg:grid-cols-3'>
					{assets.map(asset => {
						const preview = mediaPreview(asset)
						const selected = value === asset.id
						return <button key={asset.id} type='button' onClick={() => { onChange(asset.id); onOpenChange(false) }} className={`relative overflow-hidden rounded-xl border text-left transition-colors hover:bg-muted ${selected ? 'ring-2 ring-primary' : ''}`}>
							{preview ? <img src={preview} alt='' className='h-32 w-full object-cover' /> : <span className='flex h-32 items-center justify-center bg-muted'><ImageIcon className='h-7 w-7' /></span>}
							<span className='block truncate p-3 text-sm font-medium'>{asset.filename}</span>
							{selected ? <span className='absolute right-2 top-2 rounded-full bg-primary p-1 text-primary-foreground'><Check className='h-4 w-4' /></span> : null}
						</button>
					})}
				</div>
				{query.data && assets.length === 0 ? <p className='py-10 text-center text-sm text-muted-foreground'>Подходящие готовые изображения не найдены.</p> : null}
				<DialogFooter>
					<Button variant='outline' onClick={() => onOpenChange(false)}>Закрыть</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

export const ProgramExercisePicker = ({
	open,
	onOpenChange,
	onSelect,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	onSelect: (exercise: Exercise) => void
}) => {
	const api = useAdminApi()
	const [search, setSearch] = useState('')
	const query = useQuery({
		queryKey: exerciseKeys.list({ lifecycle: 'PUBLISHED', search, limit: 100, surface: 'program-builder' }),
		queryFn: async () => unwrapApiResponse(await api.GET('/admin/v1/exercises', {
			params: { query: { lifecycle: 'PUBLISHED', search: search.trim() || undefined, limit: 100, sort: 'name', direction: 'asc' } },
		})).data,
		enabled: open,
		staleTime: 15_000,
	})
	const exercises = query.data?.items ?? []

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className='max-w-4xl'>
				<DialogHeader>
					<DialogTitle>Добавить упражнение</DialogTitle>
					<DialogDescription>Выберите опубликованное упражнение из настоящего каталога.</DialogDescription>
				</DialogHeader>
				<div className='relative'>
					<Search className='pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground' />
					<Input value={search} onChange={event => setSearch(event.target.value)} placeholder='Поиск упражнения' className='pl-9' />
				</div>
				{query.isPending ? <div className='flex items-center gap-2 py-10 text-sm text-muted-foreground'><LoaderCircle className='h-4 w-4 animate-spin' /> Загрузка упражнений…</div> : null}
				{query.isError ? <p role='alert' className='rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800'>Не удалось загрузить каталог упражнений.</p> : null}
				<div className='max-h-[58vh] divide-y overflow-y-auto rounded-xl border'>
					{exercises.map(exercise => <button key={exercise.id} type='button' onClick={() => { onSelect(exercise); onOpenChange(false) }} className='flex w-full items-center gap-3 p-3 text-left transition-colors hover:bg-muted'>
						{exercise.media?.[0]?.url ? <img src={exercise.media[0].url} alt='' className='h-12 w-12 rounded-lg border object-cover' /> : <span className='flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary'><Dumbbell className='h-5 w-5' /></span>}
						<span className='min-w-0 flex-1'><span className='block truncate font-medium'>{exercise.name}</span><span className='block text-xs text-muted-foreground'>{exercise.exercise_type} · {exercise.difficulty_code ?? 'без сложности'}</span></span>
						<span className='text-xs text-muted-foreground'>v{exercise.version}</span>
					</button>)}
				</div>
				{query.data && exercises.length === 0 ? <p className='py-10 text-center text-sm text-muted-foreground'>Опубликованные упражнения не найдены.</p> : null}
				<DialogFooter><Button variant='outline' onClick={() => onOpenChange(false)}>Закрыть</Button></DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
